import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { customLocalStorage } from '@crehana/utils';
import { DEFAULT_VIDEO_ENDING_DURATION, IS_FIRST_OPENED_AI, IS_FIRST_OPENED_SKIP_AI, IS_RENDER_AI, KEY_CODES_BY_ACTION } from "../../constants";
import { useFullscreenContext } from "../Fullscreen";
import { useHlsContext } from "../Hls";
import { usePlaybackContext } from "../Playback";
import { useVolumeContext } from "../Volume";
import { UIContext } from "./UIContext";
import uiReducer, { getUIReducerInitialState } from "./uiReducer";
import { jsx as _jsx } from "react/jsx-runtime";
var UIProvider = function UIProvider(_ref) {
  var children = _ref.children,
    playerRef = _ref.playerRef,
    header = _ref.header,
    onUIVisibilityChange = _ref.onUIVisibilityChange,
    hiddenNotesProp = _ref.hiddenNotes,
    onHideNotesSwitch = _ref.onHideNotesSwitch,
    onAddNoteProp = _ref.onAddNote,
    onAutoplaySwitch = _ref.onAutoplaySwitch,
    hideLogo = _ref.hideLogo,
    hideSpeedOptions = _ref.hideSpeedOptions,
    hideSkipButtons = _ref.hideSkipButtons,
    classNotes = _ref.classNotes,
    thumbnails = _ref.thumbnails,
    pausedOverlay = _ref.pausedOverlay,
    endOverlay = _ref.endOverlay,
    onEndOverlayVisibilityChange = _ref.onEndOverlayVisibilityChange,
    onAddNoteAI = _ref.onAddNoteAI,
    onSaveNoteAI = _ref.onSaveNoteAI,
    canWarningNoteAI = _ref.canWarningNoteAI,
    visibleAI = _ref.visibleAI,
    amplitudAI = _ref.amplitudAI;
  var _usePlaybackContext = usePlaybackContext(),
    duration = _usePlaybackContext.duration,
    currentTime = _usePlaybackContext.currentTime,
    currentProgress = _usePlaybackContext.currentProgress,
    isPlaying = _usePlaybackContext.isPlaying,
    play = _usePlaybackContext.play,
    pause = _usePlaybackContext.pause,
    togglePlay = _usePlaybackContext.togglePlay,
    onBackward = _usePlaybackContext.onBackward,
    onForward = _usePlaybackContext.onForward,
    visibleSkip = _usePlaybackContext.visibleSkip,
    nextVideo = _usePlaybackContext.nextVideo;
  var _useHlsContext = useHlsContext(),
    currentQualityWarning = _useHlsContext.currentQualityWarning,
    hideQualityWarning = _useHlsContext.hideQualityWarning;
  var _useFullscreenContext = useFullscreenContext(),
    isFullscreen = _useFullscreenContext.isFullscreen,
    toggleFullscreen = _useFullscreenContext.toggleFullscreen;
  var _useVolumeContext = useVolumeContext(),
    toggleMute = _useVolumeContext.toggleMute,
    volumeUp = _useVolumeContext.volumeUp,
    volumeDown = _useVolumeContext.volumeDown;
  var _useReducer = useReducer(uiReducer, getUIReducerInitialState({
      hiddenNotes: hiddenNotesProp,
      nextVideo: nextVideo
    })),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    _useReducer2$ = _useReducer2[0],
    activeMenu = _useReducer2$.activeMenu,
    visibleUi = _useReducer2$.visibleUi,
    leaveConfirmation = _useReducer2$.leaveConfirmation,
    autoplayNext = _useReducer2$.autoplayNext,
    hiddenNotes = _useReducer2$.hiddenNotes,
    focusedPlayer = _useReducer2$.focusedPlayer,
    showGeneratedAI = _useReducer2$.showGeneratedAI,
    stateGenerateAI = _useReducer2$.stateGenerateAI,
    sourceAI = _useReducer2$.sourceAI,
    dispatch = _useReducer2[1];

  // Timeout to hide the ui
  var uiTimeout = useRef();
  var changeActiveMenu = useCallback(function (newActiveMenu) {
    dispatch({
      type: 'CHANGE_ACTIVE_MENU',
      activeMenu: newActiveMenu
    });
    // Hide quality warning if a menu is opened
    hideQualityWarning();
  }, [hideQualityWarning]);
  var hasActiveAI = useCallback(function (isActive) {
    dispatch({
      type: 'HAS_ACTIVE_AI',
      isActive: isActive
    });
  }, []);
  var hideActiveMenu = useCallback(function () {
    dispatch({
      type: 'HIDE_ACTIVE_MENU'
    });
  }, []);
  var onAddNote = useCallback(function () {
    if (onAddNoteProp) {
      dispatch({
        type: 'CHANGE_PLAYER_FOCUSED_STATE',
        focusedPlayer: false
      });
      if (activeMenu) {
        hideActiveMenu();
      }
      if (isFullscreen) {
        toggleFullscreen();
        setTimeout(function () {
          onAddNoteProp();
        }, 100);
      } else {
        onAddNoteProp();
      }
    }
    // Ingoring onAddNoteProp to avoid possible infinite changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenu, hideActiveMenu, isFullscreen, toggleFullscreen]);
  var hideUi = useCallback(function () {
    if (uiTimeout.current) {
      clearTimeout(uiTimeout.current);
    }
    dispatch({
      type: 'HIDE_UI',
      isPlaying: isPlaying,
      currentQualityWarning: currentQualityWarning
    });
  }, [currentQualityWarning, isPlaying]);
  var showUi = useCallback(function () {
    if (!leaveConfirmation) {
      dispatch({
        type: 'SHOW_UI'
      });
      if (uiTimeout.current) {
        clearTimeout(uiTimeout.current);
      }
      uiTimeout.current = window.setTimeout(function () {
        hideUi();
      }, 2000);
    }
  }, [hideUi, leaveConfirmation]);
  useEffect(function () {
    showUi();
    // Ingoring showUi because we just want to trigger this on the first render.

    return function () {
      if (uiTimeout.current) {
        clearTimeout(uiTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(function () {
    var onWindowClick = function onWindowClick(event) {
      var clickedOnPlayer = playerRef.current && playerRef.current.contains(event.target);
      dispatch({
        type: 'CHANGE_PLAYER_FOCUSED_STATE',
        focusedPlayer: !!clickedOnPlayer
      });
    };

    // Keyboard controls
    var onKeyDown = function onKeyDown(event) {
      if (focusedPlayer) {
        switch (event.code) {
          case KEY_CODES_BY_ACTION.TOGGLE_PLAY:
            togglePlay();
            break;
          case KEY_CODES_BY_ACTION.TOGGLE_MUTE:
            toggleMute();
            break;
          case KEY_CODES_BY_ACTION.VOLUME_UP:
            volumeUp();
            break;
          case KEY_CODES_BY_ACTION.VOLUME_DOWN:
            volumeDown();
            break;
          case KEY_CODES_BY_ACTION.BACKWARD:
            onBackward();
            break;
          case KEY_CODES_BY_ACTION.FORWARD:
            onForward();
            break;
          case KEY_CODES_BY_ACTION.TOGGLE_FULLSCREEN:
            toggleFullscreen();
            break;
          case KEY_CODES_BY_ACTION.ADD_NOTE:
            onAddNote();
            break;
          default:
        }
      }
    };

    // Add listeners on document
    if (typeof document !== 'undefined') {
      document.addEventListener('mousedown', onWindowClick);
      document.addEventListener('touchstart', onWindowClick);
      document.addEventListener('keydown', onKeyDown);
    }
    return function () {
      if (typeof document !== 'undefined') {
        document.removeEventListener('mousedown', onWindowClick);
        document.removeEventListener('touchstart', onWindowClick);
        document.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [playerRef, focusedPlayer, togglePlay, toggleMute, volumeUp, volumeDown, onBackward, onForward, onAddNote, toggleFullscreen]);
  useEffect(function () {
    if (onUIVisibilityChange) {
      onUIVisibilityChange(visibleUi);
    }
    // Ingoring onUIVisibilityChange to avoid possible infinite changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleUi]);
  var onMouseMove = useCallback(function () {
    if (!visibleSkip && !visibleUi) {
      showUi();
    }
  }, [visibleSkip, visibleUi, showUi]);
  var onMouseDown = useCallback(function (event) {
    var skipButton = document.querySelector('.cp-skip-desktop');
    var clickedOnSkipButton = skipButton && skipButton.contains(event.target);
    if (!clickedOnSkipButton && !visibleUi) {
      showUi();
    }
  }, [showUi, visibleUi]);
  var onTouch = useCallback(function (event) {
    var skipButton = document.querySelector('.cp-skip-mobile');
    var touchedOnSkipButton = skipButton && skipButton.contains(event.target);
    if (!touchedOnSkipButton && !visibleUi) {
      showUi();
    }
  }, [showUi, visibleUi]);
  var toggleAutoplay = useCallback(function () {
    dispatch({
      type: 'TOGGLE_AUTOPLAY_NEXT',
      onAutoplaySwitch: onAutoplaySwitch
    });
    // Ingoring onAutoplaySwitch to avoid possible infinite changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var toggleHiddenNotes = useCallback(function () {
    dispatch({
      type: 'TOGGLE_HIDDEN_NOTES',
      onHideNotesSwitch: onHideNotesSwitch
    });
    // Ingoring onHideNotesSwitch to avoid possible infinite changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var putOpenedAI = useCallback(function () {
    customLocalStorage.setItem("".concat(IS_FIRST_OPENED_AI), 'true');
  }, []);
  var setRenderAI = useCallback(function (stateRender) {
    customLocalStorage.setItem("".concat(IS_RENDER_AI), "".concat(stateRender));
  }, []);
  var getRenderAI = useCallback(function () {
    return customLocalStorage.getItem("".concat(IS_RENDER_AI));
  }, []);
  var getOpenedAI = useCallback(function () {
    return Boolean(customLocalStorage.getItem("".concat(IS_FIRST_OPENED_AI)));
  }, []);
  var putOpenedSkipAI = useCallback(function () {
    customLocalStorage.setItem("".concat(IS_FIRST_OPENED_SKIP_AI), 'true');
  }, []);
  var getOpenedSkipAI = useCallback(function () {
    return Boolean(customLocalStorage.getItem("".concat(IS_FIRST_OPENED_SKIP_AI)));
  }, []);
  var toogleGeneratedAI = useCallback(function (source) {
    dispatch({
      type: 'TOOGLE_GENERATED_AI',
      paramSource: source
    });
  }, []);
  var changeGenerateAI = useCallback(function (selectType) {
    dispatch({
      type: 'CHANGE_STATE_AI',
      paramState: selectType
    });
  }, []);
  var onBackClick = useCallback(function (e) {
    var _header$back, _header$back2;
    e.stopPropagation();
    var remainingSeconds = duration - currentTime;
    var backConfirmation = header === null || header === void 0 ? void 0 : (_header$back = header.back) === null || _header$back === void 0 ? void 0 : _header$back.confirmation;
    if (backConfirmation && currentProgress >= (backConfirmation.minVideoProgressToShow || 0.4) && remainingSeconds > DEFAULT_VIDEO_ENDING_DURATION) {
      dispatch({
        type: 'SHOW_LEAVE_CONFIRMATION'
      });
      pause();
      hideUi();
    } else if (header !== null && header !== void 0 && (_header$back2 = header.back) !== null && _header$back2 !== void 0 && _header$back2.onClick) {
      header.back.onClick(e);
    }
  },
  // Ingoring header object because we're comparing the stringified version.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
  // eslint-disable-next-line react-hooks/exhaustive-deps
  JSON.stringify(header), duration, currentTime, currentProgress, hideUi, pause]);
  var onBackConfirm = useCallback(function (e) {
    var _header$back3;
    if (header !== null && header !== void 0 && (_header$back3 = header.back) !== null && _header$back3 !== void 0 && _header$back3.onClick) {
      if (header.back.confirmation && header.back.confirmation.onAction) {
        header.back.confirmation.onAction({
          action: 'leave',
          duration: duration,
          remainingSeconds: duration - currentTime,
          currentProgress: currentProgress
        });
      }
      header.back.onClick(e);
    }
  },
  // Ingoring header object because we're comparing the stringified version.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [JSON.stringify(header), duration, currentTime, currentProgress]);
  var onBackCancel = useCallback(function () {
    var _header$back4, _header$back4$confirm;
    dispatch({
      type: 'HIDE_LEAVE_CONFIRMATION'
    });
    play();
    if (header !== null && header !== void 0 && (_header$back4 = header.back) !== null && _header$back4 !== void 0 && (_header$back4$confirm = _header$back4.confirmation) !== null && _header$back4$confirm !== void 0 && _header$back4$confirm.onAction) {
      header.back.confirmation.onAction({
        action: 'stay',
        duration: duration,
        remainingSeconds: duration - currentTime,
        currentProgress: currentProgress
      });
    }
    // Ignoring header object because we're comparing the stringified version.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(header), duration, currentTime, currentProgress, play]);
  var contextValue = useMemo(function () {
    return {
      header: header,
      hideLogo: hideLogo,
      hideSpeedOptions: hideSpeedOptions,
      hideSkipButtons: hideSkipButtons,
      classNotes: classNotes,
      onAddNote: onAddNoteProp ? onAddNote : undefined,
      thumbnails: thumbnails,
      pausedOverlay: pausedOverlay,
      endOverlay: endOverlay,
      onEndOverlayVisibilityChange: onEndOverlayVisibilityChange,
      activeMenu: activeMenu,
      visibleUi: visibleUi,
      leaveConfirmation: leaveConfirmation,
      autoplayNext: autoplayNext,
      hiddenNotes: hiddenNotes,
      changeActiveMenu: changeActiveMenu,
      hideActiveMenu: hideActiveMenu,
      showUi: showUi,
      hideUi: hideUi,
      onMouseMove: onMouseMove,
      onMouseDown: onMouseDown,
      onTouch: onTouch,
      toggleAutoplay: onAutoplaySwitch ? toggleAutoplay : undefined,
      toggleHiddenNotes: onHideNotesSwitch ? toggleHiddenNotes : undefined,
      onBackClick: onBackClick,
      onBackConfirm: onBackConfirm,
      onBackCancel: onBackCancel,
      putOpenedAI: putOpenedAI,
      setRenderAI: setRenderAI,
      getRenderAI: getRenderAI,
      getOpenedAI: getOpenedAI,
      putOpenedSkipAI: putOpenedSkipAI,
      getOpenedSkipAI: getOpenedSkipAI,
      toogleGeneratedAI: toogleGeneratedAI,
      showGeneratedAI: showGeneratedAI,
      stateGenerateAI: stateGenerateAI,
      changeGenerateAI: changeGenerateAI,
      hasActiveAI: hasActiveAI,
      visibleAI: visibleAI,
      onAddNoteAI: onAddNoteAI,
      onSaveNoteAI: onSaveNoteAI,
      canWarningNoteAI: canWarningNoteAI,
      amplitudAI: amplitudAI,
      sourceAI: sourceAI
    };
  },
  // Ingoring onAddNoteProp to avoid possible infinite changes.
  // Ingoring onAutoplaySwitch to avoid possible infinite changes.
  // Ingoring onHideNotesSwitch to avoid possible infinite changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [header, hideLogo, hideSpeedOptions, hideSkipButtons, classNotes, onAddNote, thumbnails, pausedOverlay, endOverlay, onEndOverlayVisibilityChange, activeMenu, visibleUi, leaveConfirmation, autoplayNext, hiddenNotes, changeActiveMenu, hideActiveMenu, showUi, hideUi, onMouseMove, onMouseDown, onTouch, toggleAutoplay, toggleHiddenNotes, setRenderAI, getRenderAI, onBackClick, onBackConfirm, onBackCancel, getOpenedAI, showGeneratedAI, stateGenerateAI, changeGenerateAI, hasActiveAI, visibleAI, onAddNoteAI, onSaveNoteAI, canWarningNoteAI, amplitudAI, sourceAI]);
  return /*#__PURE__*/_jsx(UIContext.Provider, {
    value: contextValue,
    children: children
  });
};
export default UIProvider;